/**
 * Derive the Reports-page scope badge content from the currently active role on
 * `/api/roles/current_user/`, which is fetched with a `base_division_id` so the
 * role's `attributes` are already scoped to the division being viewed.
 *
 * A user may hold several roles; the Reports page operates under whichever one
 * is active (persisted as `localStorage.currentRoleId`, see `getCurrentRole`),
 * so the badges reflect only that role. The BE only emits Course/Faculty
 * attribute types for scoped Viewers; the field is absent for everyone else,
 * which yields an empty scope (no badges).
 *
 * Pure function — no fetching, no React.
 */
import { ATTRIBUTE_TYPES } from '../../../consts'
import { type UserRole, type UserRoleAttribute } from '../../../models/UserRoles'

export interface AttributeScopeEntry {
  title: string
  value: string
}

export interface AppliedAttributeScope {
  courseAttributes: AttributeScopeEntry[]
  facultyAttributes: AttributeScopeEntry[]
}

const EMPTY_SCOPE: AppliedAttributeScope = {
  courseAttributes: [],
  facultyAttributes: []
}

export const deriveAppliedAttributeScope = (
  role: UserRole | undefined | null
): AppliedAttributeScope => {
  const attrs = role?.attributes
  if (attrs == null || attrs.length === 0) return EMPTY_SCOPE

  const courseAttributes: AttributeScopeEntry[] = []
  const facultyAttributes: AttributeScopeEntry[] = []

  for (const entry of attrs) {
    const target = bucketFor(entry)
    if (target != null) target.push({ title: entry.title, value: entry.value })
  }

  if (courseAttributes.length === 0 && facultyAttributes.length === 0) return EMPTY_SCOPE
  return { courseAttributes, facultyAttributes }

  function bucketFor (entry: UserRoleAttribute): AttributeScopeEntry[] | undefined {
    switch (entry.attributeType) {
      // The BE emits the human-readable type strings (ATTRIBUTE_TYPES, e.g.
      // "Course Attributes"); we also accept the enum-style values
      // (COURSE_ATTRIBUTE / FACULTY_ATTRIBUTE) in case the contract uses them.
      case ATTRIBUTE_TYPES.COURSE_ATTRIBUTES:
      case 'COURSE_ATTRIBUTE':
        return courseAttributes
      case ATTRIBUTE_TYPES.FACULTY_COURSE_ATTRIBUTES:
      case 'FACULTY_ATTRIBUTE':
        return facultyAttributes
      // Student / User types (and anything unexpected) are intentionally dropped.
      default:
        return undefined
    }
  }
}
